import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  FileWarning,
  Loader2,
  Play,
  RotateCw,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";
import { OptimizerCard } from "./optimizer-card";
import { PromptHealthBanner } from "./prompt-health-banner";
import { usePageAudience } from "./use-page-audience";

/**
 * "I · Calibração" cockpit. After the per-correction agent was removed,
 * the surface is split across three pillars:
 *
 *   - **Otimização neurosimbólica** (OptimizerCard) — the primary action.
 *     Re-fits affected ladders against the current corpus + parked
 *     corrections, with per-dim accept/reject/refit review.
 *   - **Manutenção** (engineering-only) — accuracy measurement +
 *     disk-sync. Both are diagnostic / structural, not daily-use.
 *   - **Destrutivo** — reseed the whole DB to a known state.
 *
 * The legacy "Processar correções na fila" / "Resolver aguardando" / batch
 * runs / per-correction agent UI lived here. It was deleted alongside
 * `server/labs/calibration-agent.ts`. The corrections still ship from the
 * candidate-intelligence page and land as `parked`; the optimizer is the
 * only thing that promotes them past that state.
 */

// ────────────────────────────────────────────────────────────────────────────
// Wire types
// ────────────────────────────────────────────────────────────────────────────

interface AccuracySnapshot {
  threshold: number;
  current: number | null;
  gate: number;
}

interface AccuracyHistoryEntry {
  at: string;
  rate: number;
  passed: boolean;
  misses: number;
  total: number;
}

interface BaselineStatus {
  running: boolean;
  pid?: number;
  startedAt?: string;
  logPath?: string;
  ensemble?: number;
}

interface SummaryResponse {
  queue: {
    parked: number;
    applied: number;
    parkedByDim: Record<string, number>;
    lastSettledAt: string | null;
  };
  accuracy: AccuracySnapshot;
  history: AccuracyHistoryEntry[];
  baseline: BaselineStatus;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "agora há pouco";
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days}d`;
  return d.toLocaleDateString("pt-BR");
}

function useTickWhile(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/** Live elapsed + rough ETA bar for the baseline subprocess. */
function BaselineRunningStrip({ startedAt }: { startedAt: string | null }) {
  const now = useTickWhile(true);
  if (!startedAt) return null;
  const startedMs = new Date(startedAt).getTime();
  const elapsedMs = Math.max(0, now - startedMs);
  const TYPICAL_MIN_MS = 15 * 60 * 1000;
  const TYPICAL_MAX_MS = 25 * 60 * 1000;
  const pct = Math.min(100, Math.round((elapsedMs / TYPICAL_MAX_MS) * 100));
  const elapsedLabel =
    elapsedMs < 60_000
      ? `${Math.round(elapsedMs / 1000)}s`
      : `${Math.floor(elapsedMs / 60_000)}m ${Math.round((elapsedMs % 60_000) / 1000)
          .toString()
          .padStart(2, "0")}s`;
  const remainingMs = Math.max(0, TYPICAL_MAX_MS - elapsedMs);
  const remainingMin = Math.ceil(remainingMs / 60_000);
  const overrun = elapsedMs > TYPICAL_MAX_MS;
  return (
    <div className="baseline-running-strip">
      <div className="baseline-running-strip-bar" aria-hidden>
        <div
          className={`baseline-running-strip-fill ${overrun ? "is-overrun" : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="baseline-running-strip-meta tabular-nums">
        <span>
          decorrido <strong>{elapsedLabel}</strong>
        </span>
        <span>·</span>
        <span>
          {overrun
            ? "rodando há mais do que o esperado — checar o log"
            : elapsedMs < TYPICAL_MIN_MS
              ? `~${Math.ceil((TYPICAL_MIN_MS - elapsedMs) / 60_000)}-${remainingMin} min restantes`
              : `~${remainingMin} min restantes (faixa típica)`}
        </span>
      </div>
    </div>
  );
}

function RiskBadge({
  level,
  children,
}: {
  level: "safe" | "care" | "danger";
  children: React.ReactNode;
}) {
  return <span className={`calib-op-risk-badge risk-${level}`}>{children}</span>;
}

function EffectChip({
  tone,
  children,
}: {
  tone: "cost" | "neutral" | "preserve" | "danger";
  children: React.ReactNode;
}) {
  return <li className={`calib-op-effect-chip tone-${tone}`}>{children}</li>;
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

interface CalibrationRunsSectionProps {
  discipline?: string;
}

export function CalibrationRunsSection({ discipline }: CalibrationRunsSectionProps = {}) {
  const [audience] = usePageAudience();
  const chroMode = audience === "chro";
  const { toast } = useToast();

  // Confirmation modal state for the destructive reseed path.
  const [confirmReseed, setConfirmReseed] = useState<"keep" | "reset" | null>(null);
  const [resetConfirmToken, setResetConfirmToken] = useState("");
  const RESET_CONFIRM_PHRASE = "RESTAURAR";

  const { data, isLoading } = useQuery<SummaryResponse>({
    queryKey: ["/api/labs/calibration-runs/summary"],
    queryFn: () => rpcClient.labs.calibrationRunsSummary() as Promise<SummaryResponse>,
    refetchInterval: (query) => {
      const latest = query.state.data as SummaryResponse | undefined;
      return latest?.baseline?.running ? 5000 : false;
    },
  });

  const runBaseline = useMutation({
    mutationFn: () => rpcClient.labs.calibrationRunsTriggerBaseline({ ensemble: 3, discipline }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/labs/calibration-runs/summary"] });
      toast({
        title: "Baseline iniciado",
        description: "Roda em background (~15-25 min). Você recebe um toast quando termina.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Falha ao iniciar baseline",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const reseed = useMutation({
    mutationFn: (mode: "keep" | "reset") =>
      rpcClient.labs.calibrationRunsReseed({ reset: mode === "reset" }),
    onSuccess: (_data, mode) => {
      setConfirmReseed(null);
      setResetConfirmToken("");
      queryClient.invalidateQueries({ queryKey: ["/api/labs/calibration-runs/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/labs/calibration-optimizer/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/labs/corrections-audit"] });
      toast({
        title:
          mode === "reset"
            ? "Restaurado do zero"
            : "Rubricas + prompts atualizados a partir do disco",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao restaurar", description: err.message, variant: "destructive" });
    },
  });

  // Baseline-completion toast + scroll to history.
  const baselineWasRunningRef = useRef(false);
  const notifiedBaselineAtRef = useRef<string | null>(null);
  const baselineHistoryAnchorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const isRunning = data?.baseline?.running === true;
    if (isRunning) {
      baselineWasRunningRef.current = true;
      return;
    }
    if (!baselineWasRunningRef.current) return;
    baselineWasRunningRef.current = false;
    const latestEntry = data?.history?.[0];
    if (!latestEntry || notifiedBaselineAtRef.current === latestEntry.at) return;
    notifiedBaselineAtRef.current = latestEntry.at;
    toast({
      title: latestEntry.passed ? "Baseline concluído" : "Baseline abaixo do piso",
      description: latestEntry.passed
        ? `${latestEntry.rate.toFixed(1)}% — atualizou o piso. Veja a entrada nova no histórico.`
        : `${latestEntry.rate.toFixed(1)}% — piso não foi atualizado. Veja o detalhe no histórico.`,
    });
    setTimeout(() => {
      baselineHistoryAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [data, toast]);

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { queue, accuracy, history, baseline } = data;
  const baselineRunning = baseline?.running === true;

  return (
    <div className="calibration-runs-section py-4 space-y-4" data-testid="calibration-runs-section">
      <PromptHealthBanner liveItems={[]} />

      <div
        className="rounded-md border border-border px-3 py-2.5 text-xs leading-relaxed flex items-center justify-between gap-3 flex-wrap"
        style={{
          background:
            accuracy.current === null
              ? "rgba(15,44,89,0.04)"
              : accuracy.current < accuracy.gate
                ? "rgba(244,54,76,0.06)"
                : "rgba(34,197,94,0.06)",
        }}
        data-testid="calibration-accuracy-widget"
      >
        <div>
          <strong className="text-foreground">Acurácia da calibração</strong> — piso atual é{" "}
          <strong>{accuracy.gate.toFixed(1)}%</strong> (o menor entre threshold={accuracy.threshold}
          % e último rodado {accuracy.current === null ? "—" : `${accuracy.current.toFixed(1)}%`}).
        </div>
        <div className="text-muted-foreground">
          {queue.parked > 0 ? (
            <>
              <strong className="tabular-nums">{queue.parked}</strong> correç
              {queue.parked === 1 ? "ão" : "ões"} pendente{queue.parked === 1 ? "" : "s"} · última
              aplicada {formatRelative(queue.lastSettledAt)}
            </>
          ) : (
            <>nenhuma correção pendente</>
          )}
        </div>
      </div>

      <div className="calib-ops-stack">
        {/* 01 — Otimização neurosimbólica (the primary CHRO action) */}
        <OptimizerCard />

        {/* 02 — Medir acurácia (engineering only) */}
        {!chroMode && (
          <section
            className="calib-op-card risk-safe"
            aria-labelledby="calib-op-baseline"
            data-testid="calib-op-baseline"
          >
            <header className="calib-op-head">
              <span className="calib-op-kicker">02 · medir acurácia</span>
              <h3 id="calib-op-baseline" className="calib-op-title">
                Medir acurácia do modelo
              </h3>
              <RiskBadge level="safe">só lê, não escreve</RiskBadge>
            </header>
            <p className="calib-op-body">
              <strong>Quando usar:</strong> quer saber o quanto o modelo concorda com a CHRO sem
              alterar nada. Roda o scorer contra todos os perfis do corpus e compara com os
              <code className="mono-inline"> expected_scores</code> pinados. Atualiza o piso de
              acurácia se passar — nenhum prompt é modificado.
            </p>
            <ul className="calib-op-effects">
              <EffectChip tone="cost">fixtures × 5 dim × ensemble=3 (~15–25 min)</EffectChip>
              <EffectChip tone="neutral">atualiza piso se passar</EffectChip>
              <EffectChip tone="preserve">prompts e correções intocados</EffectChip>
            </ul>
            <div className="calib-op-action-row">
              <Button
                size="sm"
                onClick={() => runBaseline.mutate()}
                disabled={runBaseline.isPending || baselineRunning}
                data-testid="run-baseline"
              >
                {runBaseline.isPending || baselineRunning ? (
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5 mr-2" />
                )}
                {baselineRunning ? "Baseline rodando…" : "Rodar baseline completo"}
              </Button>
              <span className="calib-op-cost-line">
                piso atual <strong className="tabular-nums">{accuracy.gate.toFixed(1)}%</strong>
              </span>
            </div>
            {baselineRunning && baseline && (
              <div
                className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs"
                data-testid="baseline-status"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  <strong>Baseline em execução</strong>
                  <span className="text-muted-foreground tabular-nums">pid {baseline.pid}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    iniciado {formatRelative(baseline.startedAt ?? null)}
                  </span>
                  {typeof baseline.ensemble === "number" && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">ensemble={baseline.ensemble}</span>
                    </>
                  )}
                </div>
                <BaselineRunningStrip startedAt={baseline.startedAt ?? null} />
                <div className="mt-1 text-muted-foreground">
                  Janela típica ~15-25 min. Quando terminar você recebe um toast e o histórico
                  abaixo é destacado — não precisa ficar atualizando a página.
                </div>
                {baseline.logPath && (
                  <div className="mt-1 text-muted-foreground">
                    Log: <code className="text-[11px]">{baseline.logPath}</code>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* 03 — Sincronizar com o disco (engineering only) */}
        {!chroMode && (
          <section
            className="calib-op-card risk-care"
            aria-labelledby="calib-op-maintenance"
            data-testid="calib-op-maintenance"
          >
            <header className="calib-op-head">
              <span className="calib-op-kicker">03 · sincronizar</span>
              <h3 id="calib-op-maintenance" className="calib-op-title">
                Sincronizar com o disco
              </h3>
              <RiskBadge level="care">altera prompts</RiskBadge>
            </header>
            <p className="calib-op-body">
              <strong>Quando usar:</strong> você acabou de mergear uma mudança de prompt e quer
              aplicá-la sem aguardar o próximo deploy. Reescreve{" "}
              <code className="mono-inline">scoring_config[*]</code> a partir dos arquivos
              versionados em <code className="mono-inline">docs/labs/</code>.{" "}
              <strong>Sobrescreve qualquer proposta da otimização neurosimbólica</strong> ainda não
              revertida — use só quando o disco é a fonte da verdade que você quer.
            </p>
            <ul className="calib-op-effects">
              <EffectChip tone="neutral">sobrescreve disciplinas, rubricas, prompts</EffectChip>
              <EffectChip tone="preserve">correções e avaliações intocadas</EffectChip>
              <EffectChip tone="cost">~5–10s</EffectChip>
            </ul>
            <div className="calib-op-action-row">
              <Button
                size="sm"
                onClick={() => setConfirmReseed("keep")}
                disabled={reseed.isPending}
                data-testid="reseed-keep"
              >
                {reseed.isPending && confirmReseed === "keep" ? (
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                ) : (
                  <RotateCw className="h-3.5 w-3.5 mr-2" />
                )}
                Recarregar rubricas e prompts
              </Button>
            </div>
          </section>
        )}

        {chroMode && (
          <div className="calib-emergency-rule" aria-hidden="true">
            <span>se algo deu muito errado</span>
          </div>
        )}

        {/* Destrutivo — visible to all, compact in CHRO mode */}
        <section
          className={`calib-op-card risk-danger${chroMode ? " is-compact" : ""}`}
          aria-labelledby="calib-op-reset"
          data-testid="calib-op-reset"
        >
          {chroMode ? (
            <div className="calib-op-compact-row">
              <ShieldAlert className="h-4 w-4 shrink-0 text-rose-700" aria-hidden />
              <div className="calib-op-compact-body">
                <strong>Recomeçar do zero</strong>
                <span className="calib-op-compact-hint">
                  Apaga todas as correções, avaliações e histórico. Use só se um engenheiro pediu.
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setResetConfirmToken("");
                  setConfirmReseed("reset");
                }}
                disabled={reseed.isPending}
                className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-700 shrink-0"
                data-testid="reseed-reset"
              >
                Recomeçar
              </Button>
            </div>
          ) : (
            <>
              <header className="calib-op-head">
                <span className="calib-op-kicker calib-op-kicker-danger">
                  <ShieldAlert className="h-3 w-3" aria-hidden />
                  04 · destrutivo · use com extremo cuidado
                </span>
                <h3 id="calib-op-reset" className="calib-op-title">
                  Recomeçar do zero
                </h3>
                <RiskBadge level="danger">apaga histórico</RiskBadge>
              </header>
              <p className="calib-op-body">
                Apaga <strong>todas as correções</strong>, todas as avaliações e o histórico de
                versões. Volta para o estado inicial do agente. Não dá pra desfazer — use só se um
                engenheiro pediu.
              </p>
              <ul className="calib-op-effects">
                <EffectChip tone="danger">
                  <FileWarning className="h-3 w-3" aria-hidden /> apaga candidate_corrections
                </EffectChip>
                <EffectChip tone="danger">
                  <FileWarning className="h-3 w-3" aria-hidden /> apaga labs_evaluations
                </EffectChip>
                <EffectChip tone="danger">
                  <FileWarning className="h-3 w-3" aria-hidden /> apaga config_versions
                </EffectChip>
                <EffectChip tone="danger">
                  <FileWarning className="h-3 w-3" aria-hidden /> TRUNCATE calibration_fixtures
                </EffectChip>
                <EffectChip tone="neutral">resemeia tudo do disco</EffectChip>
              </ul>
              <div className="calib-op-action-row">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setResetConfirmToken("");
                    setConfirmReseed("reset");
                  }}
                  disabled={reseed.isPending}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-700"
                  data-testid="reseed-reset-engineering"
                >
                  Restaurar e apagar tudo
                </Button>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Baseline history accordion */}
      {history.length > 0 && (
        <div
          ref={baselineHistoryAnchorRef}
          className="rounded-md border border-border overflow-hidden scroll-mt-4"
        >
          <div className="px-3 py-2 border-b border-border bg-muted/30">
            <div className="text-xs font-semibold text-muted-foreground">
              Histórico de baselines de acurácia ({history.length}{" "}
              {history.length === 1 ? "leitura" : "leituras"} · mais recente primeiro)
            </div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">
              Medições somente — o baseline não altera prompts. Mudanças de prompt acontecem na
              Otimização neurosimbólica ou em Sincronizar com o disco.
            </div>
          </div>
          <ol className="divide-y divide-border text-xs max-h-72 overflow-y-auto">
            {history.slice(0, 20).map((h, i) => (
              <HistoryRow
                // biome-ignore lint/suspicious/noArrayIndexKey: history order is stable
                key={`${h.at}-${i}`}
                entry={h}
              />
            ))}
          </ol>
        </div>
      )}

      <AlertDialog
        open={confirmReseed === "keep"}
        onOpenChange={(v) => {
          if (!v && !reseed.isPending) setConfirmReseed(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recarregar rubricas e prompts do disco?</AlertDialogTitle>
            <AlertDialogDescription>
              Reescreve <code>scoring_config[*]</code> a partir de <code>docs/labs/prompts/</code> e{" "}
              <code>docs/labs/seed/</code>. Sobrescreve qualquer proposta da otimização
              neurosimbólica que ainda esteja viva no banco. As correções persistidas e as
              avaliações antigas continuam intocadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reseed.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => reseed.mutate("keep")} disabled={reseed.isPending}>
              {reseed.isPending ? "Recarregando…" : "Recarregar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmReseed === "reset"}
        onOpenChange={(v) => {
          if (!v && !reseed.isPending) {
            setConfirmReseed(null);
            setResetConfirmToken("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar tudo e recomeçar do zero?</AlertDialogTitle>
            <AlertDialogDescription>
              Vai TRUNCATE em <code>candidate_corrections</code>, <code>labs_evaluations</code>,{" "}
              <code>config_versions</code> e <code>calibration_fixtures</code>. Não dá pra desfazer.
              Para confirmar, digite <strong>{RESET_CONFIRM_PHRASE}</strong> abaixo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={resetConfirmToken}
            onChange={(e) => setResetConfirmToken(e.target.value)}
            placeholder={RESET_CONFIRM_PHRASE}
            disabled={reseed.isPending}
            data-testid="reseed-reset-token"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reseed.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => reseed.mutate("reset")}
              disabled={reseed.isPending || resetConfirmToken !== RESET_CONFIRM_PHRASE}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              data-testid="reseed-reset-confirm"
            >
              {reseed.isPending ? "Restaurando…" : "Restaurar e apagar tudo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// History row — compact view, expands to per-fixture report fetched from disk.
// ────────────────────────────────────────────────────────────────────────────

interface BaselineReportRow {
  candidate: string;
  dim: string;
  expected: number | null;
  actual: number | null;
  deviation: number | null;
  withinTolerance: boolean | null;
  rationale?: string;
}

interface BaselineReport {
  runAt: string;
  tolerance: number;
  hitRate?: number;
  goodHitRate?: number;
  misses: number;
  total: number;
  gate?: { threshold: number; currentAtRunStart: number | null; gate: number; passed: boolean };
  rows: BaselineReportRow[];
}

function HistoryRow({ entry }: { entry: AccuracyHistoryEntry }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching, error } = useQuery<BaselineReport>({
    queryKey: ["/api/labs/calibration-runs/baseline-report", entry.at],
    queryFn: () =>
      rpcClient.labs.calibrationRunsBaselineReport({ at: entry.at }) as Promise<BaselineReport>,
    enabled: open,
    retry: false,
    staleTime: 60_000,
  });
  return (
    <li className="px-3 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-center gap-3 hover:bg-muted/30 rounded px-1 py-1"
      >
        <span className="tabular-nums text-foreground font-semibold">{entry.rate.toFixed(1)}%</span>
        <span className="text-muted-foreground tabular-nums">
          {entry.misses === 0 ? "perfeito" : `${entry.total - entry.misses}/${entry.total} acertos`}
        </span>
        <span className="text-muted-foreground">{formatRelative(entry.at)}</span>
        {entry.passed ? (
          <span className="ml-auto inline-flex items-center gap-1 text-emerald-700">
            <CheckCircle2 className="h-3 w-3" aria-hidden />
            passou
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 text-rose-700">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            abaixo do piso
          </span>
        )}
      </button>
      {open && (
        <div className="mt-2 pl-1">
          {isFetching && <p className="text-muted-foreground">Carregando relatório do disco…</p>}
          {error && (
            <p className="text-muted-foreground">
              Relatório não está disponível no disco deste ambiente.
            </p>
          )}
          {data && (
            <div className="text-[11px] leading-relaxed">
              <div className="text-muted-foreground">
                tolerance ±{data.tolerance.toFixed(2)} · {data.rows.length} fixtures
              </div>
              <table className="mt-2 w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left font-medium py-1">Candidato</th>
                    <th className="text-left font-medium py-1">Dim</th>
                    <th className="text-right font-medium py-1 tabular-nums">Alvo</th>
                    <th className="text-right font-medium py-1 tabular-nums">Modelo</th>
                    <th className="text-right font-medium py-1 tabular-nums">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.slice(0, 30).map((r) => (
                    <tr key={`${r.candidate}-${r.dim}`} className="border-b border-border/50">
                      <td className="py-1">{r.candidate}</td>
                      <td className="py-1 text-muted-foreground">{r.dim}</td>
                      <td className="py-1 text-right tabular-nums">{r.expected ?? "—"}</td>
                      <td className="py-1 text-right tabular-nums">
                        {r.actual?.toFixed(2) ?? "—"}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {r.withinTolerance === false ? (
                          <span className="text-rose-700">
                            <XCircle className="h-3 w-3 inline" aria-hidden />{" "}
                            {r.deviation?.toFixed(2) ?? "—"}
                          </span>
                        ) : (
                          <span className="text-emerald-700">{r.deviation?.toFixed(2) ?? "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
